import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';

// Place this at the very top
const isLocalStorageAvailable = () => {
  try {
    const testKey = "test";
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Basic validation
    if (!email || !password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('https://phone-tracker-backend-wqeb.onrender.com/api/login', {
        email,
        password,
      });

      console.log('Login Response:', response.data);

      if (response.data && response.data.user) {
        const userData = JSON.stringify(response.data.user);

        // Store user data and token
        if (isLocalStorageAvailable()) {
          localStorage.setItem('user', userData);
          localStorage.setItem('isLoggedIn', 'true');
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('loginTime', new Date().toISOString());
          
          // Also store token separately if needed for API calls
          if (response.data.token) {
            localStorage.setItem('authToken', response.data.token);
          }
        } else {
          sessionStorage.setItem('user', userData);
          sessionStorage.setItem('isLoggedIn', 'true');
          sessionStorage.setItem('token', response.data.token);
          alert('localStorage is not available. Using sessionStorage instead.');
        }

        // Dispatch storage event to update navigation in real-time
        window.dispatchEvent(new Event('storage'));
        
        // Show success message
        alert('✅ Login successful! Redirecting to dashboard...');
        
        // Redirect to dashboard
        navigate('/dashboard');
      } else {
        setError('Login failed: User data not provided.');
      }
    } catch (err) {
      console.error('Login Error:', err);
      if (err.response) {
        // Server responded with error
        setError(err.response.data?.message || 'Invalid email or password');
      } else if (err.request) {
        // Request made but no response
        setError('Cannot connect to server. Please check your connection.');
      } else {
        // Something else happened
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Background decoration */}
      <div style={styles.backgroundDecoration}>
        <div style={styles.decorationCircle1}></div>
        <div style={styles.decorationCircle2}></div>
      </div>

      {/* Login Card */}
      <div style={styles.loginCard}>
        {/* Logo and Header */}
        <div style={styles.header}>
          <div style={styles.logo}>
            <span style={styles.logoIcon}>📱</span>
            <span style={styles.logoText}>PhoneTracker Pro</span>
          </div>
          <h2 style={styles.welcomeText}>Welcome Back!</h2>
          <p style={styles.subtitle}>Sign in to continue tracking your devices</p>
        </div>

        {/* Error Message */}
        {error && (
          <div style={styles.errorContainer}>
            <span style={styles.errorIcon}>⚠️</span>
            <p style={styles.errorText}>{error}</p>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleLogin} style={styles.form}>
          {/* Email Field */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <span style={styles.labelIcon}>📧</span>
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={styles.input}
              placeholder="Enter your email"
              disabled={loading}
            />
          </div>

          {/* Password Field */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <span style={styles.labelIcon}>🔒</span>
              Password
            </label>
            <div style={styles.passwordContainer}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={styles.passwordInput}
                placeholder="Enter your password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.passwordToggle}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div style={styles.optionsRow}>
            <label style={styles.checkboxLabel}>
              <input type="checkbox" style={styles.checkbox} />
              <span>Remember me</span>
            </label>
            <Link to="/forgot-password" style={styles.forgotPassword}>
              Forgot password?
            </Link>
          </div>

          {/* Login Button */}
          <button 
            type="submit" 
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : {})
            }}
            disabled={loading}
          >
            {loading ? (
              <span style={styles.loadingSpinner}>
                <span style={styles.spinner}></span>
                Logging in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>

          {/* Social Login (Optional) */}
          <div style={styles.socialSection}>
            <p style={styles.socialText}>Or continue with</p>
            <div style={styles.socialButtons}>
              <button type="button" style={styles.socialButton} disabled={loading}>
                <span style={styles.socialIcon}>G</span>
              </button>
              <button type="button" style={styles.socialButton} disabled={loading}>
                <span style={styles.socialIcon}>f</span>
              </button>
              <button type="button" style={styles.socialButton} disabled={loading}>
                <span style={styles.socialIcon}>in</span>
              </button>
            </div>
          </div>
        </form>

        {/* Sign Up Link */}
        <div style={styles.signupContainer}>
          <p style={styles.signupText}>
            Don't have an account?{' '}
            <Link to="/register" style={styles.signupLink}>
              Sign up now
            </Link>
          </p>
        </div>

        {/* Demo Credentials */}
        <div style={styles.demoContainer}>
          <p style={styles.demoTitle}>🔐 Demo Credentials</p>
          <p style={styles.demoText}>Email: demo@example.com</p>
          <p style={styles.demoText}>Password: demo123</p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  backgroundDecoration: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 1,
  },
  decorationCircle1: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    top: '-100px',
    right: '-100px',
    animation: 'float 6s ease-in-out infinite',
  },
  decorationCircle2: {
    position: 'absolute',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    bottom: '-50px',
    left: '-50px',
    animation: 'float 8s ease-in-out infinite',
  },
  loginCard: {
    position: 'relative',
    zIndex: 2,
    width: '450px',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    padding: '40px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    animation: 'slideUp 0.5s ease-out',
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  logoIcon: {
    fontSize: '32px',
  },
  logoText: {
    fontSize: '24px',
    fontWeight: 'bold',
    background: 'linear-gradient(45deg, #667eea, #764ba2)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  welcomeText: {
    fontSize: '28px',
    color: '#333',
    marginBottom: '10px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#666',
    fontSize: '14px',
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '10px',
    padding: '12px',
    marginBottom: '20px',
  },
  errorIcon: {
    fontSize: '20px',
  },
  errorText: {
    color: '#c00',
    fontSize: '14px',
    margin: 0,
    flex: 1,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#555',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  labelIcon: {
    fontSize: '16px',
  },
  input: {
    padding: '12px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    fontSize: '15px',
    transition: 'all 0.3s',
    outline: 'none',
    ':focus': {
      borderColor: '#667eea',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
    },
  },
  passwordContainer: {
    position: 'relative',
    width: '100%',
  },
  passwordInput: {
    width: '100%',
    padding: '12px 45px 12px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    fontSize: '15px',
    transition: 'all 0.3s',
    outline: 'none',
    boxSizing: 'border-box',
    ':focus': {
      borderColor: '#667eea',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
    },
  },
  passwordToggle: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    padding: '5px',
    color: '#666',
    ':hover': {
      color: '#333',
    },
  },
  optionsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '14px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#666',
    cursor: 'pointer',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  forgotPassword: {
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: '500',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  button: {
    padding: '14px',
    background: 'linear-gradient(45deg, #667eea, #764ba2)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
    marginTop: '10px',
    ':hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 10px 20px rgba(102, 126, 234, 0.3)',
    },
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
    ':hover': {
      transform: 'none',
      boxShadow: 'none',
    },
  },
  loadingSpinner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderTop: '3px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    display: 'inline-block',
  },
  socialSection: {
    marginTop: '20px',
    textAlign: 'center',
  },
  socialText: {
    color: '#666',
    fontSize: '14px',
    marginBottom: '15px',
  },
  socialButtons: {
    display: 'flex',
    justifyContent: 'center',
    gap: '15px',
  },
  socialButton: {
    width: '45px',
    height: '45px',
    borderRadius: '50%',
    border: '2px solid #e0e0e0',
    background: 'white',
    cursor: 'pointer',
    transition: 'all 0.3s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      borderColor: '#667eea',
      transform: 'translateY(-2px)',
    },
  },
  socialIcon: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#555',
  },
  signupContainer: {
    textAlign: 'center',
    marginTop: '25px',
    paddingTop: '20px',
    borderTop: '1px solid #e0e0e0',
  },
  signupText: {
    color: '#666',
    fontSize: '14px',
  },
  signupLink: {
    color: '#667eea',
    textDecoration: 'none',
    fontWeight: '600',
    marginLeft: '5px',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  demoContainer: {
    marginTop: '20px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '10px',
    textAlign: 'center',
  },
  demoTitle: {
    color: '#333',
    fontWeight: '600',
    marginBottom: '8px',
    fontSize: '14px',
  },
  demoText: {
    color: '#666',
    fontSize: '13px',
    margin: '3px 0',
  },
};

// Add keyframes for animations
const style = document.createElement('style');
style.textContent = `
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-20px); }
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  input:focus {
    border-color: #667eea !important;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
  }
  
  button:hover:not(:disabled) {
    transform: translateY(-2px);
  }
`;
document.head.appendChild(style);

export default Login;